/**
 * In-app help content for BunkerOS. Plain data module (no framework code) that
 * the Drawer / Nav / Content components import. Each section has an id, title,
 * icon (Font Awesome class — the convention already used across the app), and a
 * list of topics; each topic has an id, title, and an HTML `body`.
 *
 * `getHelpLocation(pathname)` is a pure lookup mapping the current route to the
 * section + topic the drawer should open on.
 */
export const HELP_SECTIONS = [
  {
    id: 'welcome',
    title: 'Welcome',
    icon: 'fa-solid fa-compass',
    topics: [
      {
        id: 'overview',
        title: 'Overview',
        body: `
          <p><strong>BunkerOS</strong> is the sovereign operating system for physical bunker
          suppliers and barge operators — coordinating nominations, scheduling, delivery
          evidence, credit, invoicing, and disputes in one deterministic shell.</p>
          <p style="margin-top:12px;"><strong>How it fits together:</strong></p>
          <ul>
            <li><strong>Operations Console</strong> — your role-based landing dashboard with live
            KPIs, the barge Gantt monitor, and the operations wallboard.</li>
            <li><strong>Consoles</strong> — dedicated screens for credit, nominations, scheduling,
            invoicing, computations, and claims, reached from the top navigation.</li>
          </ul>
          <div style="background:#f8f7ff; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#075277; font-weight:600; margin-bottom:8px;">Two-Minds Separation</p>
            <p style="font-size:13px;">All pricing, tax, quality verdicts, and credit blocks are
            computed by the deterministic rules engine. AI features are advisory only and always
            require human confirmation.</p>
          </div>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: open this help drawer
          anytime with the <strong>?</strong> button in the top bar.</p>
        `,
      },
      {
        id: 'signing-in',
        title: 'Signing In',
        body: `
          <p>The <strong>login page</strong> is a split screen: the left pane shows what BunkerOS
          does, and the right pane is where you authenticate.</p>
          <ol>
            <li><strong>Operational Profile</strong> — pick your profile; the matching demo email is
            filled in for you. You can type a different <strong>Registered Email</strong> instead.</li>
            <li style="margin-top:8px;"><strong>Secure Verification Key</strong> — enter your
            password (at least 8 characters). Use the eye icon to reveal it.</li>
            <li style="margin-top:8px;"><strong>Initialize Session Shell</strong> — sign in. On
            success you land on the Operations Console for your role.</li>
          </ol>
          <p style="margin-top:12px;">If the email format is wrong or the password is too short, the
          field is flagged inline before submitting. If the credentials don't match, a
          <span style="color:#ef4444;">Failed credentials</span> alert appears and you stay on the
          login page.</p>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: use <strong>Log out</strong>
          in the top bar or on the dashboard header to end your session securely.</p>
        `,
      },
    ],
  },
  {
    id: 'guide',
    title: 'How To',
    icon: 'fa-solid fa-list-check',
    topics: [
      {
        id: 'operations-console',
        title: 'Operations Console',
        body: `
          <p>The <strong>Operations Console</strong> is your role-based command center. When you
          sign in, it opens on the profile view that matches your role.</p>
          <p style="margin-top:12px;"><strong>What you'll see:</strong></p>
          <ul>
            <li><strong>KPI cards</strong> — the four metrics that matter most for your role, such as
            active volume, credit holds, or dispute claims.</li>
            <li><strong>Role Dynamic Actions</strong> — quick links to the consoles you use most.</li>
            <li><strong>Barge Fleet Operations</strong> — a Gantt monitor of each barge's delivering,
            completed, and available slots.</li>
            <li><strong>Role Analytics</strong> — an interactive chart tuned to your role.</li>
            <li><strong>Active Operations Wallboard</strong> — every live stem with its status
            (<span style="color:#1e3a8a;">DELIVERING</span>,
            <span style="color:#0d9488;">COMPLETED</span>,
            <span style="color:#ef4444;">DISPUTED</span>) and a jump to its detail screen.</li>
          </ul>
          <p style="margin-top:12px;">Use the <strong>search box</strong> above the wallboard to
          filter by vessel or buyer, the <strong>language toggle</strong> to switch between English
          and Arabic (right-to-left), and the <strong>profile selector</strong> to preview another
          role's view.</p>
        `,
      },
      {
        id: 'nomination-intake',
        title: 'Nomination Intake',
        body: `
          <p>The <strong>Nomination Intake Console</strong> turns incoming stems from email or
          WhatsApp into confirmed nominations. AI extraction is <strong>advisory only</strong> — you
          always confirm before anything is written to the immutable ledger.</p>
          <ol>
            <li><strong>Review</strong> — pick an item from <strong>Inbound Pending Extractions</strong>.
            Its parsed fields load into the <strong>Nomination Extraction Editor</strong> alongside
            the raw message and an AI confidence rating.</li>
            <li style="margin-top:8px;"><strong>Check &amp; correct</strong> — verify the buyer,
            vessel <strong>IMO</strong> (exactly 7 digits), quantity, spec, pricing basis, and credit
            terms. Inline validation flags anything invalid.</li>
            <li style="margin-top:8px;"><strong>Approve Extraction</strong> — records the nomination
            as <span style="color:#0d9488;">NOMINATED</span> with a unique <strong>ledger trace
            id</strong> and a tentative quote.</li>
          </ol>
          <div style="background:#fffbeb; border:1px solid #fde68a; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#b45309; font-weight:600; margin-bottom:8px;">Guards</p>
            <p style="font-size:13px;">If the buyer has a <strong>credit hold</strong>, approval is
            locked. If the same vessel and buyer are already nominated, an
            <strong>AMENDMENT / DUPLICATE DETECTED</strong> prompt asks you to confirm — the change is
            saved as a new version, never overwriting the original.</p>
          </div>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: use <strong>Reject Draft</strong>
          to discard an extraction you don't want to process.</p>
        `,
      },
      {
        id: 'credit-compliance',
        title: 'Credit & Compliance',
        body: `
          <p>The <strong>Credit &amp; Compliance Board</strong> lets a Credit Controller track
          counterparty exposure, run the credit gate, and release holds under dual authorization.</p>
          <p style="margin-top:12px;"><strong>Registry:</strong> each buyer shows its approved
          <strong>limit</strong>, live <strong>exposure</strong>, aging buckets, sanctions screening,
          and status — <span style="color:#0d9488;">ACTIVE</span>,
          <span style="color:#f59e0b;">WATCH</span>, or
          <span style="color:#ef4444;">BLOCKED</span>. Search and the status filter narrow the list.</p>
          <p style="margin-top:12px;"><strong>Real-time Credit Gate:</strong></p>
          <ol>
            <li><strong>Pick a counterparty</strong> and enter a <strong>nomination value</strong>.</li>
            <li style="margin-top:8px;"><strong>Run Credit Gate</strong> — if the projected exposure
            breaches the limit (or sanctions aren't clear) the buyer is flagged
            <strong>CREDIT GATE BLOCKED</strong> and placed on hold.</li>
          </ol>
          <div style="background:#fffbeb; border:1px solid #fde68a; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#b45309; font-weight:600; margin-bottom:8px;">Releasing a hold (dual control)</p>
            <p style="font-size:13px;">Click <strong>Release Hold</strong>, enter a justification,
            choose a <strong>second approver</strong>, and provide both cryptographic keys (at least 8
            characters each). On success the status transitions to
            <strong>CREDIT_CLEARED</strong> and the override is written to the append-only ledger with
            a trace id. No AI ever sets limits or approves a release.</p>
          </div>
        `,
      },
      {
        id: 'organization-settings',
        title: 'Organization Settings',
        body: `
          <p>Under <strong>Organization &amp; Entities Settings</strong> an Administrator manages the
          legal entity and where deliveries can happen.</p>
          <p style="margin-top:12px;"><strong>Legal Entity Profile:</strong> edit the entity name,
          <strong>TRN</strong>, address, invoice numbering series, currencies, and fiscal calendar,
          then <strong>Save Profile</strong>. Every change is written to the ledger with a trace id,
          so the audit trail is complete.</p>
          <p style="margin-top:12px;"><strong>Delivery Locations Registry:</strong></p>
          <ol>
            <li><strong>Add Location</strong> — enter the port/anchorage name, geofence coordinates,
            timezone, and jurisdiction pack, then <strong>Save Location</strong>.</li>
            <li style="margin-top:8px;"><strong>Edit</strong> updates a location; <strong>Delete</strong>
            deactivates it so it no longer appears on new stems.</li>
          </ol>
          <p style="margin-top:12px;">The <strong>White-Label Branding</strong> panel previews your
          console colour and logos before you apply them across the console, PWA, and documents.</p>
        `,
      },
      {
        id: 'fleet-settings',
        title: 'Fleet Settings',
        body: `
          <p>Under <strong>Fleet &amp; Assets Settings</strong> an Administrator maintains the barge
          fleet and the rules scheduling depends on.</p>
          <ol>
            <li><strong>Register Barge</strong> — enter the name, MFM unit, seal register, permit,
            class survey expiry, AIS/MMSI, and speed, then register it into the asset registry.</li>
            <li style="margin-top:8px;"><strong>Publish Compatibility Matrix</strong> — click a cell
            to cycle a product transition between <span style="color:#0e8f89;">Allowed</span>,
            <span style="color:#9f6700;">Cleaning</span>, and
            <span style="color:#b43749;">Forbidden</span>, then publish. Each publish increments the
            version scheduling evaluates.</li>
          </ol>
          <div style="background:#fef2f2; border:1px solid #fecaca; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#b91c1c; font-weight:600; margin-bottom:8px;">Certificate expiry &amp; hard lock</p>
            <p style="font-size:13px;">Warning horizons default to <strong>60 / 30 / 7</strong> days
            and are configurable. When a barge's class survey has <strong>expired</strong>, its status
            shows <strong>Expired Permit</strong> and a scheduling <strong>HARD LOCK</strong> — the
            barge cannot be scheduled until the certificate is renewed.</p>
          </div>
        `,
      },
      {
        id: 'scheduling',
        title: 'Scheduling',
        body: `
          <p>The <strong>Scheduling &amp; Gantt Board</strong> is where a Dispatcher allocates
          nominated stems to barge tanks. The deterministic rule-pack — not AI — enforces every
          safety constraint.</p>
          <ol>
            <li><strong>Select a stem</strong> from <strong>Pending Nominated Stems</strong>. The
            compatible tanks in the fleet grid highlight.</li>
            <li style="margin-top:8px;"><strong>Click a tank</strong> to assign the stem. If the
            assignment is safe it is written to the ledger with a trace id.</li>
          </ol>
          <p style="margin-top:12px;"><strong>Guards enforced on every assignment:</strong></p>
          <ul>
            <li><strong>Tank compatibility</strong> — loading a product into a tank that last held an
            incompatible product is a <span style="color:#ef4444;">Tank Cross-Contamination</span>
            hard block (per the compatibility matrix).</li>
            <li><strong>95% max-fill</strong> — quantities above the safe fill factor are rejected.</li>
            <li><strong>Certificate validity</strong> — a barge whose class survey expires before the
            delivery window is blocked.</li>
          </ul>
          <div style="background:#fffbeb; border:1px solid #fde68a; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#b45309; font-weight:600; margin-bottom:8px;">Overriding a block</p>
            <p style="font-size:13px;">A blocked assignment can only proceed with a
            <strong>dual co-signed override</strong>: enter a justification and two controller keys.
            The exception is recorded on the append-only ledger.</p>
          </div>
        `,
      },
      {
        id: 'barge-execution',
        title: 'Barge Execution',
        body: `
          <p>The <strong>Barge Master Execution Screen</strong> runs the delivery clock and captures
          tamper-proof evidence — fully offline for up to 72 hours, syncing when the barge reconnects
          at anchorage.</p>
          <ol>
            <li><strong>Log milestones</strong> — tap each execution timestamp (Alongside, Hose
            Connected, Start/Stop Pumping, …) as it happens; entries queue locally when offline.</li>
            <li style="margin-top:8px;"><strong>Complete the evidence checklist</strong> — MFM
            calibration, seal verification, totaliser readings, aeration, and sample seals.</li>
            <li style="margin-top:8px;"><strong>Seal Delivery</strong> — enter the Barge Master and
            Vessel Rep countersignature PINs, then transmit the sealed evidence package.</li>
          </ol>
          <div style="background:#fef2f2; border:1px solid #fecaca; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#b91c1c; font-weight:600; margin-bottom:8px;">Totaliser continuity</p>
            <p style="font-size:13px;">Your <strong>opening non-resettable</strong> totaliser reading
            must match the prior stem's closing register <strong>exactly</strong>. Any mismatch raises
            <strong>TOTALISER DISCONTINUITY DETECTED</strong> — a tamper exception is recorded and the
            delivery is blocked from the <strong>DOCUMENTED</strong> state until it is resolved.</p>
          </div>
        `,
      },
      {
        id: 'invoicing',
        title: 'Invoicing & ERP',
        body: `
          <p>The <strong>Invoicing &amp; ERP Console</strong> lets a Finance Officer verify
          deterministic invoices and post them to SAP S/4HANA.</p>
          <ol>
            <li><strong>Fetch a stem</strong> — enter the stem id and fetch its invoice. Every line
            is computed from sealed billing facts: delivered mass × base price, plus
            <strong>UAE Standard VAT (5%)</strong>. No AI touches pricing or tax.</li>
            <li style="margin-top:8px;"><strong>Verify the totals</strong> — taxable subtotal, VAT,
            and net total due.</li>
            <li style="margin-top:8px;"><strong>Approve &amp; Post to SAP ERP</strong> — enter the SAP
            cost center and post. Posts use idempotent keys, so a stem is never double-billed.</li>
          </ol>
          <div style="background:#fef2f2; border:1px solid #fecaca; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#b91c1c; font-weight:600; margin-bottom:8px;">Dead-letter queue</p>
            <p style="font-size:13px;">If a post fails, the invoice drops into the
            <strong>ERP Synchronization Queue</strong> with status
            <strong>DEAD_LETTER</strong> and raises an operations alarm. Use <strong>Retry Post</strong>
            to re-send it once the connection is restored.</p>
          </div>
        `,
      },
      {
        id: 'quality-audit',
        title: 'Quality Audit',
        body: `
          <p>The <strong>Computations &amp; Quality Auditor</strong> checks lab certificate values
          against the governing ISO 8217 edition using deterministic ISO 4259 margins.</p>
          <ol>
            <li><strong>Pick the governing edition</strong> and enter the lab results in the
            <strong>Quality Parameter Evaluator</strong> table (result, spec max, reproducibility R).</li>
            <li style="margin-top:8px;"><strong>Compare Specifications</strong> — each parameter is
            marked:
            <span style="color:#0e8f89;">PASS</span> (within spec),
            <span style="color:#9f6700;">MARGINAL</span> (within the reproducibility band
            <em>spec ± 0.59·R</em>), or
            <span style="color:#b43749;">FAIL</span>.</li>
          </ol>
          <p style="margin-top:12px;">The <strong>Quantity Calculator</strong> derives the VCF and
          delivered mass and flags an aeration plausibility issue when the implied density diverges
          by more than 0.5%.</p>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: switch editions to see how the
          same lab result can shift between compliant and marginal.</p>
        `,
      },
      {
        id: 'claims',
        title: 'Claims & Disputes',
        body: `
          <p>The <strong>Claims &amp; Disputes Manager</strong> lets a Claims Analyst track disputes
          against their time bars and assemble legal-ready evidence.</p>
          <ul>
            <li><strong>Registry</strong> — search and filter claims by status; select one to load its
            details, state timeline, linked evidence tags, and advisory draft.</li>
            <li><strong>Response Draft Editor</strong> — refine the AI-advisory rebuttal, then
            <strong>Confirm &amp; Seal Draft</strong> to lock it to the evidence ledger.</li>
          </ul>
          <div style="background:#f8f7ff; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#075277; font-weight:600; margin-bottom:8px;">One-Click Sealed Export</p>
            <p style="font-size:13px;">Generate a tamper-evident <strong>ZIP index</strong> bundling
            every ledger record, signature receipt, and sample-custody event. The bundle is signed
            with a cryptographic hash so a legal counsel can verify it has not been altered.</p>
          </div>
        `,
      },
      {
        id: 'rules-packs',
        title: 'Rules Packs',
        body: `
          <p>Under <strong>Rules Packs Administration</strong> an Administrator governs the
          deterministic rules that drive pricing and verdicts — with a human-controlled rollout.</p>
          <ol>
            <li><strong>Upload New Rules Pack</strong> — choose the domain and jurisdiction, then
            <strong>Verify &amp; Stage</strong>. The pack's <strong>checksum</strong> and
            <strong>signature</strong> are verified; a pack that fails verification cannot be staged.</li>
            <li style="margin-top:8px;"><strong>Review the diff</strong> — the Version Diff Viewer lists
            each changed, added, or unchanged rule vs the active pack.</li>
            <li style="margin-top:8px;"><strong>Run the Impact Preview</strong> — see the pricing and
            verdict deltas the staged pack would produce across a sample of recent stems.</li>
            <li style="margin-top:8px;"><strong>Schedule Effective Date &amp; Propose Activation</strong>
            — set the effective date and open the co-sign workflow.</li>
          </ol>
          <div style="background:#f8f7ff; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#075277; font-weight:600; margin-bottom:8px;">Dual approval</p>
            <p style="font-size:13px;">Activating a staged pack is
            <strong>DUAL_APPROVAL_PROPOSED</strong> — an Administrator and a Finance Officer must
            co-sign. Auto-activation is never enabled, and stamped versions on open stems are never
            restamped.</p>
          </div>
        `,
      },
      {
        id: 'integrations',
        title: 'Integrations',
        body: `
          <p>Under <strong>Integrations Settings</strong> an Administrator monitors every external
          connector (ERP, e-BDN, AIS, WhatsApp, screening, index feeds) and keeps the stem lifecycle
          resilient.</p>
          <ul>
            <li><strong>Status cards</strong> show each connector as
            <span style="color:#0e8f89;">Connected</span>,
            <span style="color:#9f6700;">Degraded</span>,
            <span style="color:#6b31d8;">Configured</span>,
            <span style="color:#b43749;">Failed</span>, or
            <span style="color:#94a3b8;">Disabled</span>.</li>
            <li><strong>Test Connection</strong> runs a handshake and logs the latency/result on the card.</li>
          </ul>
          <div style="background:#fff8ec; border:1px solid #ffa800; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#9f6700; font-weight:600; margin-bottom:8px;">Graceful fallback</p>
            <p style="font-size:13px;">When you <strong>disable</strong> an integration it never blocks
            operations — the card switches to <strong>Disabled</strong>, a notification is raised, and
            the <strong>manual fallback</strong> (e.g. <em>Export CSV Invoice</em>) becomes an active,
            accessible action.</p>
          </div>
          <p style="margin-top:12px;">Under <strong>Mapping Tables Version Control</strong>, publishing
          a new version preserves the mapping used by existing postings — new postings adopt the new
          version.</p>
        `,
      },
      {
        id: 'operational-parameters',
        title: 'Operational Parameters',
        body: `
          <p>The <strong>Operational Parameters Registry</strong> is the single source of truth for
          every tunable value — max-fill factors, tolerances, k-factors, aging thresholds, and cadences.</p>
          <ol>
            <li><strong>Edit Parameter</strong> — numeric values are <strong>range-validated</strong>
            against the permitted bounds, and a justification is required.</li>
            <li style="margin-top:8px;"><strong>Propose Change</strong> — a
            <strong>Single-Approval</strong> parameter commits immediately; a
            <strong>Dual-Approval</strong> parameter (money- or verdict-gating) is routed to a holding
            registry to await co-sign.</li>
          </ol>
          <div style="background:#f8f7ff; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#075277; font-weight:600; margin-bottom:8px;">New evaluations only</p>
            <p style="font-size:13px;">Any accepted change applies strictly to
            <strong>future evaluations</strong> — sealed historical results are never recomputed.</p>
          </div>
        `,
      },
      {
        id: 'notifications',
        title: 'Notifications & Routing',
        body: `
          <p>Under <strong>Notifications &amp; Templates Settings</strong> an Administrator manages
          bilingual message templates and the internal alert-routing matrix.</p>
          <ul>
            <li><strong>Template Registry</strong> — per event class, channel, and language, with BSP
            approval status and a <strong>Test Send</strong> action.</li>
            <li><strong>Internal Alert Routing</strong> — each exception class maps to specific
            <strong>target roles</strong> and <strong>channels</strong> (Console, Email, WhatsApp).</li>
          </ul>
          <p style="margin-top:12px;">Use <strong>Test Send</strong> to fire a sample alert and confirm
          it reaches the right people — a <strong>Time-bar Alarm</strong> routes to the
          <strong>Dispatcher</strong> and <strong>Barge Master</strong> via <strong>Console/Email</strong>.</p>
        `,
      },
      {
        id: 'buyer-portal',
        title: 'Buyer Portal',
        body: `
          <p>Under <strong>Buyer Portal Administration</strong> an Administrator governs the
          client-facing portal centrally.</p>
          <ul>
            <li><strong>Portal Master Switch</strong> — turning the portal <strong>off</strong>
            terminates active buyer sessions and queues scheduled publications; turning it
            <strong>on</strong> requires the <strong>activation checklist</strong> (branding, disclosure
            matrix, notification channel) to validate first.</li>
            <li><strong>Counterparty Enablement</strong> — grant or revoke portal access per buyer.
            Disabling a counterparty marks its outstanding <strong>invitations inactive</strong>.</li>
            <li><strong>Invitation Management</strong> — see accepted, pending, and deactivated buyer
            users at a glance.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: the access-mix chart shows how
          many counterparties are currently enabled vs disabled.</p>
        `,
      },
      {
        id: 'approvals',
        title: 'Users & Co-signing',
        body: `
          <p>Under <strong>Users, Roles &amp; Approvals Settings</strong> an authorized secondary role
          reviews and co-signs the changes that require dual authorization.</p>
          <ol>
            <li><strong>Pending Dual-Approval Proposals</strong> — money- and verdict-gating changes
            wait here in a holding state until co-signed.</li>
            <li style="margin-top:8px;"><strong>Review Proposal</strong> — compare the setting key, the
            old value and its hash, the proposed value, the proposer, and the timestamp.</li>
            <li style="margin-top:8px;"><strong>Co-sign Change &amp; Execute</strong> — enter your
            secure PIN and confirm.</li>
          </ol>
          <div style="background:#f8f7ff; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#075277; font-weight:600; margin-bottom:8px;">On co-sign</p>
            <p style="font-size:13px;">The proposal becomes <strong>APPROVED</strong>, the setting is
            committed to the active registry, and a <strong>ledger audit event</strong> is written.
            The mandate table lists which actions are permanently dual-gated.</p>
          </div>
        `,
      },
    ],
  },
];

// Route → { sectionId, topicId } the drawer opens on for the current page.
const ROUTE_MAP = {
  '/login': { sectionId: 'welcome', topicId: 'signing-in' },
  '/operations-console': { sectionId: 'guide', topicId: 'operations-console' },
  '/nomination-intake-console': { sectionId: 'guide', topicId: 'nomination-intake' },
  '/credit-compliance-board': { sectionId: 'guide', topicId: 'credit-compliance' },
  '/organization-entities-settings': { sectionId: 'guide', topicId: 'organization-settings' },
  '/fleet-assets-settings': { sectionId: 'guide', topicId: 'fleet-settings' },
  '/scheduling-gantt-board': { sectionId: 'guide', topicId: 'scheduling' },
  '/barge-master-execution-screen': { sectionId: 'guide', topicId: 'barge-execution' },
  '/invoicing-erp-console': { sectionId: 'guide', topicId: 'invoicing' },
  '/computations-quality-auditor': { sectionId: 'guide', topicId: 'quality-audit' },
  '/claims-disputes-manager': { sectionId: 'guide', topicId: 'claims' },
  '/rules-packs-administration': { sectionId: 'guide', topicId: 'rules-packs' },
  '/integrations-settings': { sectionId: 'guide', topicId: 'integrations' },
  '/operational-parameters-registry': { sectionId: 'guide', topicId: 'operational-parameters' },
  '/notifications-templates-settings': { sectionId: 'guide', topicId: 'notifications' },
  '/buyer-portal-administration': { sectionId: 'guide', topicId: 'buyer-portal' },
  '/users-roles-approvals-settings': { sectionId: 'guide', topicId: 'approvals' },
};

export function getHelpLocation(pathname) {
  if (ROUTE_MAP[pathname]) return ROUTE_MAP[pathname];
  return { sectionId: 'welcome', topicId: 'overview' };
}

export default HELP_SECTIONS;
